import type { Car, CostLine, KoppelTijdlijnItem, Meting, Tarief } from '@fleet/shared';
import {
  TARIEF_SOORTEN,
  alleTariefWijzigingen,
  apkInfo,
  catToBucket,
  euro,
  fmtDate,
  perMaand,
} from '@fleet/shared';

/** What a timeline entry lets you do, when it is something we entered ourselves. */
export interface TimelineActie {
  soort: 'meting' | 'tarief';
  id: string;
  /** Confirmation line, so deleting says what disappears. */
  omschrijving: string;
  /**
   * Rates only: whether an earlier period will take over when this one goes. When
   * it will not, deleting removes the fixed cost from every month it covered, and
   * the confirmation has to say so instead of promising a fallback.
   */
  heeftVoorganger?: boolean;
}

export interface TimelineEvent {
  date: string;
  label: string;
  detail: string;
  color: string;
  /** Present on hand-entered items: those can be removed from here. */
  actie?: TimelineActie;
}

const TL_COLOR: Record<string, string> = {
  aanschaf: '#2A6085',
  onderhoud: '#B0543B',
  mobiliteit: '#E0A943',
  apk: '#111111',
  archief: '#888888',
  koppeling: '#3E7D63',
  meting: '#6E9AA5',
  tarief: '#8C6D3F',
};

/** Human label per rate soort; unknown soorten keep their own name. */
const TARIEF_LABEL: Record<string, string> = Object.fromEntries(
  TARIEF_SOORTEN.map((s) => [s.soort, s.label]),
);

const PERIODE_LABEL: Record<string, string> = {
  MAAND: 'per maand',
  KWARTAAL: 'per kwartaal',
  JAAR: 'per jaar',
};

const km = (n: number) => `${Math.round(n).toLocaleString('nl-NL')} km`;

/**
 * Build the per-car timeline: aanschaf, onderhoud, APK, archief, plus everything
 * we entered ourselves — hand-entered odometer readings and rate changes.
 *
 * Losse tankpastransacties horen hier niet: dat zijn er tientallen per auto en ze
 * zeggen iets over de bestuurder, niet over de auto. Die staan op de persoon, in
 * de tijdlijn naast de momenten waarop hij de auto en de pas kreeg.
 *
 * Hand-entered items carry an `actie`, so the timeline is also where they are
 * removed. That is deliberate: it is the one place that already shows them in
 * context, so a reading or a rate does not need a management screen of its own.
 * Pump readings deliberately stay out: there are dozens per car, and they are
 * already visible as transactions on the person who drove it.
 */
export function detailTimeline(
  car: Car,
  costLines: CostLine[],
  koppelTijdlijn: KoppelTijdlijnItem[],
  apkReminderDays: number,
  metingen: Meting[] = [],
  tarieven: Tarief[] = [],
): TimelineEvent[] {
  const raw: {
    iso: string | null;
    type: string;
    label: string;
    detail: string;
    actie?: TimelineActie;
  }[] = [];

  // Link/unlink moments (person ↔ car) — always shown as key events.
  for (const k of koppelTijdlijn) {
    raw.push({ iso: k.datum, type: 'koppeling', label: k.label, detail: k.detail });
  }

  if (car.aanschafdatum) {
    const extra = car.aanschafwaarde
      ? ` · ${euro(car.aanschafwaarde)}`
      : car.catalogusprijs
        ? ` · cat. ${euro(car.catalogusprijs)}`
        : '';
    raw.push({
      iso: car.aanschafdatum,
      type: 'aanschaf',
      label: 'Aangekocht',
      detail: `${car.merk} ${car.model}${extra}`,
    });
  }

  // Maintenance: always shown, one event per invoice on its own date.
  for (const r of costLines.filter((c) => catToBucket(c.categorie) === 'onderhoud')) {
    const beurt = r.omschrijving || r.product || 'Werkplaatsbeurt';
    const parts = [beurt];
    if (r.station) parts.push(r.station);
    parts.push(euro(r.bedragExcl));
    raw.push({ iso: r.datum, type: 'onderhoud', label: 'Onderhoud', detail: parts.join(' · ') });
  }

  // Hand-entered odometer readings, removable from here.
  for (const m of metingen.filter((x) => x.bron === 'HAND')) {
    const parts = [km(m.waarde)];
    if (!m.betrouwbaar) parts.push('afgekeurd');
    raw.push({
      iso: m.datum,
      type: 'meting',
      label: 'Kilometerstand ingevoerd',
      detail: parts.join(' · '),
      actie: {
        soort: 'meting',
        id: m.id,
        omschrijving: `de kilometerstand van ${km(m.waarde)} op ${fmtDate(m.datum)}`,
      },
    });
  }

  // Rate changes: the first one on record reads as "recorded", the ones after it
  // as a change, with the difference per month next to it.
  //
  // Deliberately just the amount and the delta. The `notitie` of a rate carries
  // the migration's own bookkeeping ("Grootboek 5608, gefactureerd 85,86,86,85;
  // tarief van ... teruggetrokken naar de aanschafdatum"), which is worth keeping
  // in the database but turns the timeline into a wall of text. Whoever needs the
  // provenance can read it on the rate itself.
  for (const w of alleTariefWijzigingen(tarieven)) {
    const naam = TARIEF_LABEL[w.tarief.soort] ?? w.tarief.soort;
    const parts = [`${euro(w.tarief.bedrag)} ${PERIODE_LABEL[w.tarief.periode] ?? ''}`.trim()];
    if (w.deltaPerMaand != null && Math.abs(w.deltaPerMaand) >= 0.005) {
      // Zonder "per maand" erachter: dat staat al in het bedrag ervoor, en twee
      // keer dezelfde eenheid in één regel leest als een fout.
      parts.push(`${w.deltaPerMaand > 0 ? '+' : '−'} ${euro(Math.abs(w.deltaPerMaand))}`);
    }
    raw.push({
      iso: w.datum,
      type: 'tarief',
      label: w.vorige ? `${naam} gewijzigd` : `${naam} vastgelegd`,
      detail: parts.join(' · '),
      actie: {
        soort: 'tarief',
        id: w.tarief.id,
        omschrijving: `het ${naam.toLowerCase()}-tarief van ${euro(perMaand(w.tarief))} per maand vanaf ${fmtDate(w.datum)}`,
        // De service heropent de voorganger alleen als die direct aansluit; sluit
        // hij niet aan, dan valt er een gat en neemt niets het over.
        heeftVoorganger: w.vorige != null && w.vorige.geldigTot === w.tarief.geldigVan,
      },
    });
  }

  if (car.apkVervaldatum) {
    const a = apkInfo(car.apkVervaldatum, apkReminderDays);
    const detail =
      a.dagen != null
        ? a.dagen < 0
          ? 'Verlopen — plan direct'
          : `Over ${a.dagen} dagen`
        : 'Vervaldatum';
    raw.push({ iso: car.apkVervaldatum, type: 'apk', label: 'APK-keuring vereist', detail });
  }

  if (car.status !== 'ACTIEF' && car.status !== 'GARAGE') {
    raw.push({
      iso: null,
      type: 'archief',
      label: `Gearchiveerd — ${car.status}`,
      detail: car.archiefNotitie || 'Uit actief wagenpark gehaald',
    });
  }

  return raw
    .map((e) => ({ ...e, ts: e.iso ? new Date(`${e.iso}T00:00:00`).getTime() : Infinity }))
    .sort((a, b) => b.ts - a.ts)
    .map((e) => ({
      date: e.iso ? fmtDate(e.iso) : 'Recent',
      label: e.label,
      detail: e.detail,
      color: TL_COLOR[e.type] ?? '#888888',
      actie: e.actie,
    }));
}
